import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { useToast } from "../../hooks/use-toast";
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  CheckCircle,
  XCircle,
  Search,
  Globe,
} from "lucide-react";
import { supabase } from "../../utils/supabaseClient";

interface Domain {
  id: string;
  domain: string;
  description?: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface DomainFormData {
  domain: string;
  description: string;
}

const DomainManagement: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [formData, setFormData] = useState<DomainFormData>({
    domain: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const { toast } = useToast();

  // Load domains
  const loadDomains = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("domain_whitelist")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDomains(data || []);
    } catch (error) {
      console.error("Error loading domains:", error);
      toast({
        title: "Error",
        description: "Failed to load domains",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDomains();
  }, []);

  // Filter domains based on search
  const filteredDomains = domains.filter(
    (domain) =>
      domain.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.domain.trim()) {
      toast({
        title: "Error",
        description: "Domain is required",
        variant: "destructive",
      });
      return;
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(formData.domain.trim())) {
      toast({
        title: "Error",
        description: "Please enter a valid domain (e.g., company.com)",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      if (editingDomain) {
        // Update existing domain
        const { error } = await supabase
          .from("domain_whitelist")
          .update({
            domain: formData.domain.toLowerCase().trim(),
            description: formData.description.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingDomain.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Domain updated successfully",
        });
      } else {
        // Add new domain
        const { error } = await supabase.from("domain_whitelist").insert({
          domain: formData.domain.toLowerCase().trim(),
          description: formData.description.trim() || null,
        });

        if (error) {
          if (error.code === "23505") {
            // Unique constraint violation
            toast({
              title: "Error",
              description: "This domain already exists",
              variant: "destructive",
            });
            return;
          }
          throw error;
        }

        toast({
          title: "Success",
          description: "Domain added successfully",
        });
      }

      // Reset form and close dialog
      setFormData({ domain: "", description: "" });
      setShowAddDialog(false);
      setEditingDomain(null);
      loadDomains();
    } catch (error) {
      console.error("Error saving domain:", error);
      toast({
        title: "Error",
        description: "Failed to save domain",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle domain status
  const toggleDomainStatus = async (domain: Domain) => {
    try {
      const { error } = await supabase
        .from("domain_whitelist")
        .update({
          is_active: !domain.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq("id", domain.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Domain ${!domain.is_active ? "activated" : "deactivated"}`,
      });

      loadDomains();
    } catch (error) {
      console.error("Error updating domain status:", error);
      toast({
        title: "Error",
        description: "Failed to update domain status",
        variant: "destructive",
      });
    }
  };

  // Delete domain
  const deleteDomain = async (domain: Domain) => {
    if (!confirm(`Are you sure you want to delete "${domain.domain}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("domain_whitelist")
        .delete()
        .eq("id", domain.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Domain deleted successfully",
      });

      loadDomains();
    } catch (error) {
      console.error("Error deleting domain:", error);
      toast({
        title: "Error",
        description: "Failed to delete domain",
        variant: "destructive",
      });
    }
  };

  // Handle edit
  const handleEdit = (domain: Domain) => {
    setEditingDomain(domain);
    setFormData({
      domain: domain.domain,
      description: domain.description || "",
    });
    setShowAddDialog(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setShowAddDialog(false);
    setEditingDomain(null);
    setFormData({ domain: "", description: "" });
  };

  const stats = {
    total: domains.length,
    active: domains.filter((d) => d.is_active).length,
    inactive: domains.filter((d) => !d.is_active).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="bg-primary/10 p-2 rounded-lg">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Domain Whitelist</h1>
          <p className="text-muted-foreground">
            Manage allowed email domains for user registration
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Total Domains</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-red-600">{stats.inactive}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search domains..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingDomain ? "Edit Domain" : "Add New Domain"}
              </DialogTitle>
              <DialogDescription>
                {editingDomain
                  ? "Update the domain information"
                  : "Add a new domain to the whitelist"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="domain">Domain *</Label>
                  <Input
                    id="domain"
                    placeholder="e.g., company.com"
                    value={formData.domain}
                    onChange={(e) =>
                      setFormData({ ...formData, domain: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Optional description (e.g., Company Name)"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDialogClose}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting
                    ? "Saving..."
                    : editingDomain
                    ? "Update"
                    : "Add Domain"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Domains Table */}
      <Card>
        <CardHeader>
          <CardTitle>Whitelisted Domains</CardTitle>
          <CardDescription>
            Domains that are allowed for user registration
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading domains...</div>
          ) : filteredDomains.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? "No domains match your search" : "No domains added yet"}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDomains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell className="font-medium">{domain.domain}</TableCell>
                    <TableCell>
                      {domain.description || (
                        <span className="text-muted-foreground">No description</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={domain.is_active ? "default" : "secondary"}
                        className={
                          domain.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }
                      >
                        {domain.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(domain.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleDomainStatus(domain)}
                        >
                          {domain.is_active ? (
                            <XCircle className="h-4 w-4" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(domain)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteDomain(domain)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DomainManagement;
